import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { FC, ReactElement, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { User } from 'src/models/user/_types';
import { checkRequiredItems } from 'src/models/user/user';
import { isAdmin, isSupportRole } from 'src/models/user/utils';
import AppFrameLayout from 'src/pages/AppFrameLayout';
import FindClientsPage from 'src/pages/Clients/ClientsListPage/FindClientsPage';
import DialogScrollTest from 'src/pages/DialogScrollTest';
import EmbedPage from 'src/pages/Embed';
import ErrorPage from 'src/pages/Error';
import EventsPage from 'src/pages/Events/EventsPage';
import { IndexPage } from 'src/pages/IndexPage';
import { LeaderboardFormPage } from 'src/pages/LeaderboardsPage/LeaderboardFormPage';
import { LeaderboardsPage } from 'src/pages/LeaderboardsPage/LeaderboardsPage';
import { TestPage } from 'src/pages/PosConfig/TestPage';
import SMSSetupPage from 'src/pages/SMSSetupPage';
import { LoginPage } from 'src/pages/Session/LoginPage';
import { ResetPasswordPage } from 'src/pages/Session/ResetPasswordPage';
import { SignupPage } from 'src/pages/Session/SignupPage';
import RolesManagementPage from 'src/pages/Staff/RolesManagement/RolesManagementPage';
import ThemeColors from 'src/pages/ThemeColors';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';

import Theme from '../pages/Theme';

const AccessDevicePage = lazy(
  () => import('src/pages/AccessDevices/AccessDevicePage')
);
const AdminRoutes = lazy(() => import('./Admin/AdminRoutes'));
const AppointmentsPage = lazy(
  () => import('src/pages/Appointments/AppointmentsPage')
);
const BenchmarksPage = lazy(() => import('src/pages/Benchmarks/page'));
const BusinessWaiver = lazy(() => import('src/pages/BusinessWaiver'));
const CheckInPage = lazy(() => import('src/pages/CheckIn/CheckInPage'));
const ClientRoutes = lazy(() => import('./Client/ClientRoutes'));
const ClientsPage = lazy(
  () => import('src/pages/Clients/ClientsListPage/ClientsPage')
);
const ConnectionsFailurePage = lazy(
  () => import('src/pages/Connections/ConnectionsFailurePage')
);
const ConnectionsPage = lazy(
  () => import('src/pages/Connections/ConnectionsPage')
);
const ConnectionsSuccessPage = lazy(
  () => import('src/pages/Connections/ConnectionsSuccessPage')
);
const DashboardPage = lazy(() => import('src/pages/Dashboard/DashboardPage'));
const DataLoadsPage = lazy(() => import('src/pages/DataLoads/DataLoadsPage'));
const DeveloperPage = lazy(() => import('src/pages/Developer/DeveloperPage'));
const EmailRoutes = lazy(() => import('./Email/EmailRoutes'));
const ExerciseRoutes = lazy(() => import('./Exercise/ExerciseRoutes'));
const GroupRoutes = lazy(() => import('./Group/GroupRoutes'));
const LifecycleRoutes = lazy(() => import('./Lifecycle/LifecycleRoutes'));
const LinksPage = lazy(() => import('src/pages/Links/LinksPage'));
const LiveReportRoutes = lazy(() => import('./Report/LiveReportRoutes'));
const MeRoutes = lazy(() => import('./Me/MeRoutes'));
const MessagesPage = lazy(() => import('src/pages/Messages/MessagesPage'));
const MobileAppointment = lazy(
  () => import('src/pages/Appointments/MobileAppointment')
);
const MobileCheckin = lazy(() => import('src/pages/CheckIn/MobileCheckin'));
const MyPackagesPage = lazy(() => import('../pages/MyPackages/MyPackagesPage'));
const NewVideosPage = lazy(
  () => import('src/pages/Videos/NewVideos/NewVideosPage')
);
const OnboardingRoutes = lazy(() => import('./Onboarding/OnboardingRoutes'));
const PrintReportPage = lazy(() => import('src/pages/Reports/PrintReportPage'));
const ProductsRoutes = lazy(() => import('./Products/ProductsRoutes'));
const ProfileCompletionPage = lazy(
  () => import('src/pages/ProfileCompletion/ProfileCompletionPage')
);
const ReportExportPage = lazy(
  () => import('src/pages/Reports/ReportExportPage')
);
const ReportPage = lazy(() => import('src/pages/Reports/ReportPage'));
const ReportsPage = lazy(() => import('src/pages/Reports/ReportsPage'));
const RoutinesPage = lazy(() => import('src/pages/Routines/RoutinesPage'));
const ScheduleRoutes = lazy(() => import('./Schedule/ScheduleRoutes'));
const SettingsLayout = lazy(() => import('src/pages/Platform/SettingsLayout'));
const SiteConfigurationRoutes = lazy(
  () => import('./SiteConfiguration/SiteConfigurationRoutes')
);
const SitesRoutes = lazy(() => import('./Sites/SitesRoutes'));
const StaffRoutes = lazy(() => import('./Staff/StaffRoutes'));
const StripeRoutes = lazy(() => import('./Stripe/StripeRoutes'));
const SupportPage = lazy(() => import('src/pages/Support/SupportPage'));
const TagsPage = lazy(() => import('src/pages/Tags/TagsPage'));
const TimeClockPage = lazy(
  () => import('src/pages/TimeTracking/TimeClockPage')
);
const TimeTrackingPage = lazy(
  () => import('src/pages/TimeTracking/TimeTrackingPage')
);
const V2EmbedRoutes = lazy(() => import('./V2Embed/V2EmbedRoutes'));
const VideosPage = lazy(() => import('src/pages/Videos/VideosPage'));
const WorkoutPlansPage = lazy(
  () => import('src/pages/WorkoutPlans/WorkoutPlansPage')
);
const PurchaseRoutes = lazy(() => import('./Purchase/PurchaseRoutes'));

export default function AppRoutes(): ReactElement {
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  return (
    <Routes>
      {/* Non protected & non app frame routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sign-up" element={<SignupPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="connections-success" element={<ConnectionsSuccessPage />} />
      <Route path="connections-failure" element={<ConnectionsFailurePage />} />
      <Route path="/purchase/*" element={<PurchaseRoutes />} />

      <Route element={<ProtectedRoute />}>
        <Route index element={<IndexPage />} />
        <Route element={<AppFrameLayout />}>
          <Route path="/theme" element={<Theme />} />
          <Route path="/theme/colors" element={<ThemeColors />} />
          <Route path="/theme/dialog-test" element={<DialogScrollTest />} />

          {/* Nested route files */}
          <Route path="/onboarding/*" element={<OnboardingRoutes />} />
          <Route path="/exercises/*" element={<ExerciseRoutes />} />
          <Route path="/lifecycle/*" element={<LifecycleRoutes />} />
          <Route path="/products/*" element={<ProductsRoutes />} />
          <Route path="/staff/*" element={<StaffRoutes />} />
          <Route path="/me/*" element={<MeRoutes />} />
          <Route path="/groups/*" element={<GroupRoutes />} />
          <Route path="/client/*" element={<ClientRoutes />} />
          <Route path="/fbm/*" element={<ScheduleRoutes />} />
          <Route path="/v2Embed/*" element={<V2EmbedRoutes />} />
          <Route path="/sites/*" element={<SitesRoutes />} />
          <Route
            path="/site-configuration/*"
            element={<SiteConfigurationRoutes />}
          />
          <Route
            path="/settings/:tabName/:id?/edit?"
            element={<SettingsLayout />}
          />
          <Route path="/emails/*" element={<EmailRoutes />} />
          <Route path="/stripe/*" element={<StripeRoutes />} />

          {/* Clients */}
          <Route path="clients/" element={<ClientsPage />} />
          <Route path="clients/find" element={<FindClientsPage />} />
          <Route path="clients/:clientId/*" element={<ClientRoutes />} />

          {/* Reports */}
          <Route path="/reports/live/*" element={<LiveReportRoutes />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/report/:reportType" element={<ReportPage />} />
          <Route
            path="/report/:reportType/print"
            element={<PrintReportPage />}
          />
          <Route
            path="/report/:reportType/export"
            element={<ReportExportPage />}
          />

          {/* Connections */}
          <Route path="connections" element={<ConnectionsPage />} />

          {/* Embed */}
          <Route
            path="/embed/"
            element={<Navigate to="/embed/dashboard" replace />}
          />
          <Route path="/embed/*" element={<EmbedPage />} />

          {/* Videos */}
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/videos/:videoId" element={<NewVideosPage />} />

          {/* Leaderboards */}
          <Route path="/leaderboards" element={<LeaderboardsPage />} />
          <Route path="/leaderboards/new" element={<LeaderboardFormPage />} />
          <Route
            path="/leaderboards/:id/edit"
            element={<LeaderboardFormPage />}
          />

          {/* Misc pages without significant child pages */}
          <Route path="/support" element={<SupportPage />} />
          <Route path="/dashboard/" element={<DashboardPage />} />
          <Route path="/tags" element={<TagsPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route
            path="/messages/:conversationId?/:messageId?"
            element={<MessagesPage />}
          />
          <Route path="/time-tracking" element={<TimeTrackingPage />} />

          <Route path="/data-loads" element={<DataLoadsPage />} />
          <Route path="/roles-management" element={<RolesManagementPage />} />
          <Route path="/pos-config/test" element={<TestPage />} />
          <Route path="/benchmarks" element={<BenchmarksPage />} />
          <Route path="/emails/*" element={<EmailRoutes />} />
          <Route path="/stripe/*" element={<StripeRoutes />} />

          <Route path="workout-plans" element={<WorkoutPlansPage />} />
          <Route path="routines" element={<RoutinesPage />} />
          <Route path="links" element={<LinksPage />} />
        </Route>
        {/* OUTSIDE APP FRAME */}
        <Route path="/admin/*" element={<AdminRoutes />} />
        {/* Admin route has own app frame */}
        <Route path="/checkin" element={<CheckInPage />} />
        <Route path="/appointments/:filter?" element={<AppointmentsPage />} />
        <Route path="/my-packages/:filter?" element={<MyPackagesPage />} />
        <Route path="/mobile/checkin" element={<MobileCheckin />} />
        <Route path="/mobile/appointment" element={<MobileAppointment />} />
        <Route path="/developers" element={<DeveloperPage />} />
        <Route path="/time-clock" element={<TimeClockPage />} />
        <Route
          path="/access-devices/:id?/edit?"
          element={<AccessDevicePage />}
        />
        <Route path="/business-waiver" element={<BusinessWaiver />} />
        <Route path="/sms" element={<SMSSetupPage />} />
      </Route>

      <Route element={<ProtectedRoute isProfileCompletion={true} />}>
        <Route path="/profile-completion" element={<ProfileCompletionPage />} />
      </Route>

      {/* Error handling/catch routes */}
      <Route
        path="*"
        element={
          <ErrorPage type={404} title={tCommon('errors.pageNotFound')} />
        }
      />
      <Route path="401" element={<UnauthorizedPage />} />
    </Routes>
  );
}

interface ProtectedRouteProps {
  adminOnly?: boolean;
  redirectPath?: string;
  children?: ReactElement;
  isProfileCompletion?: boolean;
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  adminOnly = false,
  children,
  isProfileCompletion = false,
}) => {
  const { user, userLoading, sessionLoading, trainerLoading } =
    useOptionalSession();
  const { isWebview } = useNativeBridge();
  const location = useLocation();
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const permissions = useHQPermissionCheck();

  const { data: requiredItemsData, isLoading: checkingRequiredItems } =
    useQuery(['requiredItems', user?.id], () => checkRequiredItems(), {
      enabled: !!user?.id && !adminOnly && !isWebview,
      retry: false,
      cacheTime: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
    });

  if (
    userLoading ||
    sessionLoading ||
    checkingRequiredItems ||
    trainerLoading
  ) {
    return (
      <Box
        component="main"
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#ffffff',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#1976d2' }} />
        <Typography
          align="center"
          sx={{
            color: '#666666',
            mt: 5,
          }}
        >
          {tCommon('labels.loading')}
        </Typography>
      </Box>
    );
  }

  if (!isLoggedIn({ user, adminOnly })) {
    return (
      <Navigate
        to="/login"
        state={{
          unauthorizedMessage: adminOnly
            ? tCommon('errors.adminAccess')
            : tCommon('errors.notLoggedIn'),
          from: location.pathname + location.search,
        }}
        replace
      />
    );
  }

  const authResult = isAuthorized({ location, permissions });
  if (!authResult.authorized) {
    return (
      <Navigate
        to="/401"
        state={{
          customMessage:
            authResult.message || tCommon('errors.insufficientPermissions'),
        }}
        replace
      />
    );
  }

  if (requiredItemsData?.redirect && !isProfileCompletion) {
    const baseUrl = import.meta.env.BASE_URL; // Get base URL from Vite config
    const currentPath =
      `${baseUrl}${location.pathname}${location.search}`.replace('//', '/');
    const currentUrl = `${window.location.protocol}//${window.location.host}${currentPath}`;

    if (typeof requiredItemsData.redirect === 'string') {
      const redirectUrl = new URL(
        requiredItemsData.redirect,
        externalLinks.railsBase
      );

      redirectUrl.searchParams.append('redirect_to', currentUrl);

      window.location.replace(redirectUrl.toString());
      return null;
    } else if (typeof requiredItemsData.redirect === 'object') {
      const { path, params = {}, type } = requiredItemsData.redirect;
      const searchParams = new URLSearchParams({
        ...params,
        redirect_to: currentUrl,
      });
      const search = String(path).split('?')[1] || '';
      const profileCompletionPath =
        '/profile-completion/' + (search ? '?' + search : '');

      if (type === 'waiver') {
        window.location.replace(
          `${externalLinks.base}${profileCompletionPath}${
            String(path).includes('?') ? '&' : '?'
          }${searchParams}`
        );
        return null;
      }

      window.location.replace(
        `${externalLinks.railsBase}${path}${
          String(path).includes('?') ? '&' : '?'
        }${searchParams}`
      );
      return null;
    }
  }

  return children ? children : <Outlet />;
};

function isLoggedIn({
  user,
  adminOnly,
}: {
  user: User | undefined | null;
  adminOnly: boolean;
}): boolean {
  if (adminOnly) {
    return !!user && (isAdmin(user) || isSupportRole(user));
  } else {
    if (!user?.id) return false;
    return !adminOnly || user?.admin;
  }
}

function isAuthorized({ location, permissions }): {
  authorized: boolean;
  message?: string;
} {
  if (location.pathname.startsWith('/dashboard')) {
    return {
      authorized: permissions.canViewHQDashboard(),
      message: "You don't have permission to access the dashboard",
    };
  }
  if (
    location.pathname.startsWith('/reports') ||
    location.pathname.startsWith('/report/')
  ) {
    return {
      authorized: permissions.canViewHQReports(),
      message: "You don't have permission to access reports",
    };
  }
  if (location.pathname.startsWith('/data-loads')) {
    return {
      authorized: permissions.canManageHQDataLoads(),
      message: "You don't have permission to access data loads",
    };
  }
  if (location.pathname.startsWith('/staff')) {
    return {
      authorized: permissions.canManageHQStaff(),
      message: "You don't have permission to access staff management",
    };
  }
  return { authorized: true };
}

function UnauthorizedPage(): ReactElement {
  const location = useLocation();
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  const customMessage = location.state?.customMessage;

  return (
    <ErrorPage
      type={401}
      title={tCommon('errors.notAuthorized')}
      message={customMessage || tCommon('errors.insufficientPermissions')}
    />
  );
}
